/**
 * The switch between the local World and a server's (docs/ONLINE.md). Two ways in:
 * - the open online trial (net/trial.ts): a build made with `VITE_TRIAL=1` asks for a name before the game loads and
 *   passes the server here (`online`), in a production build too;
 * - development: open the game with `?server=ws://localhost:8787` (after `npm run server`).
 * Without either the World is returned untouched and the game runs entirely in the browser, as always.
 *
 * The game loop (main.ts) talks to the World through `apply`, `step` and `drainEvents`. With a
 * server, those three go through a `NetWorld` on this one instance, and everything that reads the
 * World (renderer, HUD, panels) reads the replica it keeps up to date.
 */
import type { World } from '@nine/shared';
import { NetWorld, type HelloInfo } from './netWorld';

export function withServer(world: World, online?: { url: string; hello: HelloInfo } | null): World {
  let url = online?.url;
  if (!url && import.meta.env.DEV) url = new URLSearchParams(location.search).get('server') ?? undefined;
  if (!url) return world;
  if (!/^wss?:\/\//.test(url)) {
    console.warn(`[net] the server wants a ws:// or wss:// address, not "${url}"; playing locally`);
    return world;
  }
  const local = { apply: world.apply.bind(world), drainEvents: world.drainEvents.bind(world) };
  const net = new NetWorld(world, local, url, online?.hello);
  Object.assign(world, {
    apply: (cmd: Parameters<World['apply']>[0]) => net.apply(cmd),
    step: (dt: number) => net.step(dt),
    drainEvents: () => net.drainEvents(),
  });
  Object.assign(window, { __net: net });
  return world;
}
